import {
  BufferAttribute,
  BufferGeometry,
  Camera,
  LinearFilter,
  Mesh,
  Scene,
  ShaderMaterial,
  Vector2,
  WebGLRenderTarget,
  type PerspectiveCamera,
  type WebGLRenderer,
} from 'three';

export type PostFxTier = 'off' | 'grade' | 'full';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const FINAL_FRAG = /* glsl */ `
  uniform sampler2D uScene;
  uniform sampler2D uBloom;
  uniform float uBloomStrength;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uScene, vUv).rgb;
    if (uBloomStrength > 0.0) c += texture2D(uBloom, vUv).rgb * uBloomStrength;
    float vignette = smoothstep(0.86, 0.22, length(vUv - 0.5));
    c *= mix(0.82, 1.02, vignette);
    c = pow(max(c + 0.008, 0.0), vec3(1.0 / 1.03)) * vec3(1.015, 1.0, 0.985);
    const mat4 bayer = mat4(
      0.0, 8.0, 2.0, 10.0,
      12.0, 4.0, 14.0, 6.0,
      3.0, 11.0, 1.0, 9.0,
      15.0, 7.0, 13.0, 5.0
    );
    int bx = int(mod(gl_FragCoord.x, 4.0));
    int by = int(mod(gl_FragCoord.y, 4.0));
    c += (bayer[bx][by] / 16.0 - 0.5) / 255.0;
    gl_FragColor = vec4(c, 1.0);
    #include <colorspace_fragment>
  }
`;

const DOWN_FRAG = /* glsl */ `
  uniform sampler2D uInput;
  uniform vec2 uTexel;
  uniform float uThreshold;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uInput, vUv + vec2(-uTexel.x, -uTexel.y)).rgb;
    c += texture2D(uInput, vUv + vec2( uTexel.x, -uTexel.y)).rgb;
    c += texture2D(uInput, vUv + vec2(-uTexel.x,  uTexel.y)).rgb;
    c += texture2D(uInput, vUv + vec2( uTexel.x,  uTexel.y)).rgb;
    c *= 0.25;
    gl_FragColor = vec4(max(c - uThreshold, 0.0), 1.0);
  }
`;

export interface PostFx {
  tier: PostFxTier;
  setTier(tier: PostFxTier): void;
  resize(width: number, height: number, dpr: number): void;
  render(scene: Scene, camera: PerspectiveCamera): void;
  dispose(): void;
}

export function createPostFx(renderer: WebGLRenderer, initialTier: PostFxTier): PostFx {
  const target = new WebGLRenderTarget(1, 1, {
    depthBuffer: true,
    minFilter: LinearFilter,
    magFilter: LinearFilter,
  });
  const half = new WebGLRenderTarget(1, 1, {
    depthBuffer: false,
    minFilter: LinearFilter,
    magFilter: LinearFilter,
  });
  const quarter = new WebGLRenderTarget(1, 1, {
    depthBuffer: false,
    minFilter: LinearFilter,
    magFilter: LinearFilter,
  });
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
  geometry.setAttribute('uv', new BufferAttribute(new Float32Array([0, 0, 2, 0, 0, 2]), 2));

  const down = new ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: DOWN_FRAG,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
    uniforms: {
      uInput: { value: target.texture },
      uTexel: { value: new Vector2(1, 1) },
      uThreshold: { value: 0.72 },
    },
  });
  const final = new ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FINAL_FRAG,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
    uniforms: {
      uScene: { value: target.texture },
      uBloom: { value: quarter.texture },
      uBloomStrength: { value: initialTier === 'full' ? 0.32 : 0 },
    },
  });
  const screenScene = new Scene();
  const screenCamera = new Camera();
  const triangle = new Mesh(geometry, final);
  triangle.frustumCulled = false;
  screenScene.add(triangle);
  let tier = initialTier;

  return {
    get tier() {
      return tier;
    },
    setTier(next) {
      tier = next;
      final.uniforms.uBloomStrength!.value = next === 'full' ? 0.32 : 0;
    },
    resize(width, height, dpr) {
      const w = Math.max(1, Math.floor(width * dpr));
      const h = Math.max(1, Math.floor(height * dpr));
      target.setSize(w, h);
      half.setSize(Math.max(1, w >> 1), Math.max(1, h >> 1));
      quarter.setSize(Math.max(1, w >> 2), Math.max(1, h >> 2));
    },
    render(scene, camera) {
      if (tier === 'off') {
        renderer.setRenderTarget(null);
        renderer.render(scene, camera);
        return;
      }
      renderer.setRenderTarget(target);
      renderer.render(scene, camera);
      if (tier === 'full') {
        triangle.material = down;
        down.uniforms.uInput!.value = target.texture;
        down.uniforms.uThreshold!.value = 0.72;
        (down.uniforms.uTexel!.value as Vector2).set(1 / target.width, 1 / target.height);
        renderer.setRenderTarget(half);
        renderer.render(screenScene, screenCamera);
        down.uniforms.uInput!.value = half.texture;
        // The first pass extracts highlights; the second only widens them.
        // Thresholding twice erased nearly all bloom on real-world luminance.
        down.uniforms.uThreshold!.value = 0;
        (down.uniforms.uTexel!.value as Vector2).set(1 / half.width, 1 / half.height);
        renderer.setRenderTarget(quarter);
        renderer.render(screenScene, screenCamera);
      }
      triangle.material = final;
      renderer.setRenderTarget(null);
      renderer.render(screenScene, screenCamera);
    },
    dispose() {
      geometry.dispose();
      down.dispose();
      final.dispose();
      target.dispose();
      half.dispose();
      quarter.dispose();
    },
  };
}
